/**
 * executiveScope — pure helpers for the /admin executive KPI row's scope captions.
 *
 * Card 3658. `ExecutiveKpiRow` renders the First Order Value scope caption (card 2457)
 * directly above the six executive tiles (card 2254). Measured on the live endpoint
 * 2026-08-29, ONE `GET /admin/analytics/executive` response carries both shapes and they
 * are DIFFERENT POPULATIONS:
 *
 *     figure          six tiles (top level)    caption block (data.firstOrderValue.totals)
 *     grossRevenue    6765.11                  7105.41
 *     netRevenue      6088.60                  6394.87
 *     profit          4693.24                  5310.57
 *     margin          0.7708241631902244       0.8304422138370288
 *     totalMembers    4377                     no First Order Value counterpart exists
 *     newMembers       931                     no First Order Value counterpart exists
 *
 * 🔴 THE CAPTION CORRECTLY SCOPES **ZERO** OF THE SIX TILES — not four of six.
 *   Two of the tiles are member COUNTS, which are not a money quantity at all, and all
 *   four money tiles carry the full-term figure rather than the caption's. So the obvious
 *   repair — slide the caption down so it only covers the money tiles — narrows the false
 *   claim from six tiles to four and fixes NOTHING. That had to be measured to be known.
 *
 * ⛔ THIS FILE OWNS NO BUSINESS COPY. The population's name arrives as the API's own
 *   `firstOrderValue.label`, and the tile names arrive as `EXECUTIVE_KPI_DEFS[].label`.
 *   Card 2457 criterion 3 pins a tree-wide `grep -rc` of the rendered caption literal
 *   (the metric name followed by an em dash) over `web/` at 0 — the wording lives in the
 *   server's constants so the UI and the endpoint cannot drift into two different
 *   definitions of one business metric. Passing both in as parameters keeps that true and
 *   makes these functions testable by EXECUTION rather than by source text.
 *
 * 🪤 ⛔ DO NOT QUOTE THAT LITERAL IN THIS FILE, NOT EVEN INSIDE A COMMENT EXPLAINING IT.
 *   The first draft of the paragraph above spelled the guarded string out, and that alone
 *   took the tree-wide count from 0 to 1 across 10,033 files — a comment ABOUT the guard
 *   silently breaking the guard. The repair is the wording here, never a narrower probe:
 *   a probe taught to ignore comments would stop seeing a real hardcoded caption too.
 *
 * 🔑 The precedent is card 2455, which hit this exact reader-error on the Telegram
 *   surface: two money blocks over different populations in one message, where a bare
 *   `Basis: …` beneath them "reads as governing BOTH, which is precisely how a reader
 *   concludes one of the two margins is wrong". Its fix was to QUALIFY the label rather
 *   than to move, split or drop the block. This is the same fix in the mirror direction —
 *   here only one of the two populations is labelled at all, so the single caption on
 *   screen is the only label a reader can attach to the unlabelled figures below it.
 */

/**
 * Pure. `['A', 'B', 'C']` → `'A, B and C'`. `null` — never `''` — when there is no usable
 * label, so the caller renders nothing rather than a dangling sentence fragment.
 */
export const formatTileLabelList = (labels) => {
  if (!Array.isArray(labels)) return null;
  const clean = labels
    .filter((label) => typeof label === 'string' && label.trim() !== '')
    .map((label) => label.trim());
  if (clean.length === 0) return null;
  if (clean.length === 1) return clean[0];
  return `${clean.slice(0, -1).join(', ')} and ${clean[clean.length - 1]}`;
};

/**
 * Pure. The sentence that stops the scope caption from being read as a header for the
 * executive tiles, built from the API's population name and the tiles' own labels.
 *
 * Returns `null` when either side is missing. That degrade matters: a notice naming an
 * empty tile list, or one that cannot name the population it belongs to, would be a
 * second confident-looking untruth on a caption whose whole defect is a confident-looking
 * untruth. Rendering nothing leaves the pre-card-3658 behaviour, which is merely
 * ambiguous rather than wrong.
 *
 * 🔑 THE SENTENCE NAMES THE TILES, ⛔ NEVER THEIR DIRECTION (card 4310). It used to say
 *   "the tiles BELOW", which was true only while the caption sat above the KPI row. Card
 *   4310 moves the caption inside the chart card — where the six tiles are ABOVE it — and
 *   card 4311 then reorders the page again, so any directional word is a claim about
 *   layout that this pure function cannot check and that goes silently false on the next
 *   reshuffle. The tile LIST is already in the sentence and identifies them unambiguously
 *   from anywhere on the page; the direction was never carrying information.
 */
export const formatOtherPopulationNotice = (populationLabel, tileLabels) => {
  const list = formatTileLabelList(tileLabels);
  if (list === null) return null;
  if (typeof populationLabel !== 'string' || populationLabel.trim() === '') return null;
  return `${populationLabel.trim()} does not scope the executive tiles: ${list} are a different population.`;
};
